import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FileText, Plus, Pencil, Trash2, Printer, ArrowRightLeft } from "lucide-react";
import { useAuth } from "@/AuthContext";
import { API } from "@/config";

export const EstimatesList = () => {
  const navigate = useNavigate();
  const [estimates, setEstimates] = useState([]);

  useEffect(() => {
    fetchEstimates();
  }, []);

  const fetchEstimates = async () => {
    try {
      const response = await axios.get(`${API}/estimates`);
      setEstimates(response.data);
    } catch (error) {
      toast.error("Error fetching estimates");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this estimate?")) {
      try {
        await axios.delete(`${API}/estimates/${id}`);
        toast.success("Estimate deleted successfully");
        fetchEstimates();
      } catch (error) {
        toast.error("Error deleting estimate");
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent': return 'bg-blue-100 text-blue-800';
      case 'accepted': return 'bg-green-100 text-green-800';
      case 'expired': return 'bg-red-100 text-red-800';
      case 'converted': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-4xl font-bold" data-testid="estimates-title">Estimates</h1>
        <Button onClick={() => navigate('/estimates/create')} data-testid="create-estimate-btn">
          <Plus className="h-4 w-4 mr-2" />
          Create Estimate
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Estimate #</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Valid Until</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {estimates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    No estimates yet. Create your first estimate!
                  </TableCell>
                </TableRow>
              ) : (
                estimates.map((estimate) => (
                  <TableRow key={estimate.id} data-testid={`estimate-row-${estimate.id}`}>
                    <TableCell className="font-medium">{estimate.estimate_number}</TableCell>
                    <TableCell>{estimate.customer_name}</TableCell>
                    <TableCell>${estimate.total.toFixed(2)}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(estimate.status)}`} data-testid={`estimate-status-${estimate.id}`}>
                        {estimate.status}
                      </span>
                    </TableCell>
                    <TableCell>
                      {estimate.valid_until ? new Date(estimate.valid_until).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell>{new Date(estimate.created_at).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/estimates/${estimate.id}`)}
                        data-testid={`view-estimate-${estimate.id}`}
                      >
                        <FileText className="h-4 w-4" />
                      </Button>
                      {estimate.status !== 'converted' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/estimates/${estimate.id}/edit`)}
                          data-testid={`edit-estimate-${estimate.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(estimate.id)}
                        data-testid={`delete-estimate-${estimate.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

